import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { LocaleService } from '../../i18n/locale.service';

@Component({
  selector: 'app-page-search-panel',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  template: `
    <section class="page-search-panel">
      <div class="search-header">
        <button type="button" class="search-toggle" (click)="toggleCollapsed()" [attr.aria-expanded]="!collapsedState()">
          <i class="pi" [class.pi-chevron-right]="collapsedState()" [class.pi-chevron-down]="!collapsedState()"></i>
          <span>{{ title }}</span>
          <small>{{ activeFiltersLabel || (activeFilters + ' ' + defaultActiveFiltersSuffix) }}</small>
        </button>

        <div class="search-actions">
          <p-button [label]="clearLabel || defaultClearLabel" icon="pi pi-filter-slash" severity="secondary" [outlined]="true" (onClick)="clear.emit()"></p-button>
          <p-button [label]="searchLabel || defaultSearchLabel" icon="pi pi-search" (onClick)="search.emit()"></p-button>
        </div>
      </div>

      @if (!collapsedState()) {
        <div class="search-body">
          @if (resultText) {
            <div class="search-summary">{{ resultText }}</div>
          }
          <ng-content></ng-content>
        </div>
      }
    </section>
  `,
  styles: [`
    .page-search-panel {
      padding: 1rem;
      border: 1px solid var(--app-border, #dfe7f3);
      border-radius: 8px;
      background: var(--app-surface, #ffffff);
      box-shadow: 0 10px 24px var(--app-shadow-soft, rgba(15, 23, 42, 0.05));
    }

    .search-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
    }

    .search-toggle {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.55rem;
      min-width: 0;
      color: var(--app-text, #172033);
      border: 0;
      background: transparent;
      cursor: pointer;
      font: inherit;
      font-weight: 700;
      padding: 0;
      text-align: left;
    }

    .search-toggle i {
      color: var(--app-muted-soft, #8aa1c1);
      font-size: 0.9rem;
    }

    .search-toggle small,
    .search-summary {
      color: var(--app-muted, #64748b);
      font-size: 0.875rem;
      font-weight: 600;
    }

    .search-actions {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    .search-body {
      padding-top: 1rem;
    }

    @media (max-width: 560px) {
      .page-search-panel {
        padding: 0.85rem;
      }

      .search-header {
        align-items: stretch;
        flex-direction: column;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageSearchPanelComponent {
  @Input() title = 'Search';
  @Input() activeFilters = 0;
  @Input() resultText = '';
  @Input() clearLabel?: string;
  @Input() searchLabel?: string;
  @Input() activeFiltersLabel?: string;
  @Input() set collapsed(value: boolean) {
    this.collapsedState.set(value);
  }
  @Output() search = new EventEmitter<void>();
  @Output() clear = new EventEmitter<void>();

  private readonly localeService = inject(LocaleService, { optional: true });
  collapsedState = signal(true);

  get defaultClearLabel(): string {
    const locale = this.localeService?.locale();
    if (locale === 'ar') return 'إلغاء التصفية';
    if (locale === 'ur') return 'فلٹرز صاف کریں';
    return 'Clear';
  }

  get defaultSearchLabel(): string {
    const locale = this.localeService?.locale();
    if (locale === 'ar') return 'بحث';
    if (locale === 'ur') return 'تلاش کریں';
    return 'Search';
  }

  get defaultActiveFiltersSuffix(): string {
    const locale = this.localeService?.locale();
    if (locale === 'ar') return 'فلاتر نشطة';
    if (locale === 'ur') return 'فعال فلٹرز';
    return 'active filters';
  }

  toggleCollapsed(): void {
    this.collapsedState.update(value => !value);
  }
}
